import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Contact.css';

const aiMessages = [
  {
    id: 1,
    text: "Hello! I'm your Clarity2Work agent. 👋",
    delay: 0.5,
  },
  {
    id: 2,
    text: "Ready to help you remember, organize, and execute.",
    delay: 1.5,
  },
  {
    id: 3,
    text: "To get started, simply send us an email.",
    delay: 2.5,
  },
  {
    id: 4,
    text: "We personally guide every early user.",
    delay: 3.5,
  },
];

function Contact() {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedMessages, setDisplayedMessages] = useState([]);

  useEffect(() => {
    if (currentMessageIndex < aiMessages.length) {
      setIsTyping(true);
      const timer = setTimeout(() => {
        setDisplayedMessages((prev) => [...prev, aiMessages[currentMessageIndex]]);
        setIsTyping(false);
        setCurrentMessageIndex((prev) => prev + 1);
      }, aiMessages[currentMessageIndex].delay * 1000);

      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex]);

  const cardVariants = {
    initial: { opacity: 0, y: 30, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
  };

  const messageVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
  };

  const typingVariants = {
    animate: {
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 1,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <section id="contact" className="landing-contact">
      <div className="landing-contact-container">
        <motion.h2
          className="landing-contact-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Get Your Agent Started
        </motion.h2>

        <motion.div
          className="landing-contact-card"
          variants={cardVariants}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.6 }}
        >
          <div className="landing-contact-ai-header">
            <div className="landing-contact-ai-avatar">
              <motion.div
                className="landing-contact-ai-pulse"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <span className="landing-contact-ai-icon">⚡</span>
              </motion.div>
            </div>
            <div className="landing-contact-ai-info">
              <span className="landing-contact-ai-name">CLARITY2WORK</span>
              <span className="landing-contact-ai-status">Agent Online</span>
            </div>
          </div>

          <div className="landing-contact-chat">
            <AnimatePresence>
              {displayedMessages.map((message) => (
                <motion.div
                  key={message.id}
                  className="landing-contact-message landing-contact-message--ai"
                  variants={messageVariants}
                  initial="initial"
                  animate="animate"
                  transition={{ delay: 0.1 }}
                >
                  <p className="landing-contact-message-text">{message.text}</p>
                </motion.div>
              ))}
            </AnimatePresence>

            {isTyping && (
              <motion.div
                className="landing-contact-typing"
                variants={typingVariants}
                animate="animate"
              >
                <span></span>
                <span></span>
                <span></span>
              </motion.div>
            )}
          </div>

          <motion.div
            className="landing-contact-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 4.5, duration: 0.6 }}
          >
            <p className="landing-contact-cta-text">
              To begin your journey, simply drop us an email.
              <br />
              We personally guide early users and help set up their first intelligent agent.
            </p>
            <motion.a
              href="mailto:info@clarity2work.com"
              className="landing-contact-email"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              info@clarity2work.com
            </motion.a>
            <p className="landing-contact-subtext">We read every message.</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Contact;
